import {HttpParams} from "@angular/common/http";

import {TableConfig} from "@app/shared/util/table-config.utils";
import {AbstractControl, UntypedFormControl, ValidationErrors, ValidatorFn} from "@angular/forms";
import {JWTUser} from "@models/jwt-data.model";
import {TicketOperationStatus} from "@models/enums/ticket-operation-status.enum";
import * as uuidv4 from 'uuid';

export class AppUtils {
    public static COPY_STRING = ' - Copy';
    public static selectedPlantsIdsFromStorage = [];
    static plantDropdownRoles = [
        'ATOM_Operator',
        'ATOM_Storage',
        'ATOM_Service'
    ];

    static buildPageableParams(tableConfig: TableConfig): HttpParams {
        let params = new HttpParams();

        if (tableConfig) {
            params = params.set('page', `${tableConfig.pager.page}`);
            params = params.set('pageSize', `${tableConfig.pager.pageSize}`);
            if (tableConfig.sorter.field) {
                params = params.set('sortBy', `${tableConfig.sorter.field}`);
                params = params.set('IsSortingAscending', `${tableConfig.sorter.IsSortingAscending}`);
            }

            tableConfig.filters.forEach(filter => {
                if (filter.value !== 'All' && filter.value !== 'Toate') {
                    params = params.set(filter.field, filter.value);
                }
            });

            tableConfig.groupers.forEach(group => {
                params = params.append("groupBy", group.value);
            });
        }
        return params;
    }

    static buildPageableParamsObject(tableConfig: TableConfig): any {
        const params: any = {};
        if (tableConfig !== null) {
            params.page = tableConfig.pager.page;
            params.pageSize = tableConfig.pager.pageSize;

            if (tableConfig.sorter.field) {
                params.sortBy = tableConfig.sorter.field;
                params.IsSortingAscending = tableConfig.sorter.IsSortingAscending;
            }

            tableConfig.filters.forEach(filter => {
                if (filter.value !== "All" && filter.value !== "Toate") {
                    params[filter.field] = filter.value;
                }
            });

            tableConfig.groupers.forEach(group => {
                params.groupBy = group.value;
            });
        }
        return params;
    }

    static buildFilterParams(tableConfig: TableConfig): HttpParams {
        let params = new HttpParams();

        if (tableConfig.filters.length === 0) {
            return params;
        }

        tableConfig.filters.forEach(filter => {
            params = params.set(filter.field, filter.value);
        });
        return params;
    }


    static notEmptyValidation(control: UntypedFormControl): { [s: string]: boolean } {
        if (control.value && !(control.value.length > 0)) {
            return {'picklist.atLeastOne': true};
        }
        return null;
    }

    static atLeastOneToolValidator(size: number): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            const forbidden = control.value < size;
            return forbidden ? {'atLeastOneTool': {value: control.value}} : null;
        };
    }

    static toolHasToBeSelected(size: number): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {

            let forbidden;

            // Checks if the value of the control is not null
            if (control.value != null) {
                forbidden = control.value.length < size;
            }

            return forbidden ? {'toolMustBeScanned': {value: control.value}} : null;
        };
    }

    static buildPageableParamsFromList(config: TableConfig): HttpParams {
        let params = new HttpParams();

        if (config) {
            params = params.set('page', `${config.pager.page}`);
            params = params.set('pageSize', `${config.pager.pageSize}`);
            if (config.sorter.field) {
                params = params.set('sortBy', `${config.sorter.field}`);
                params = params.set('IsSortingAscending', `${config.sorter.IsSortingAscending}`);
            }

            config.filters.forEach(filter => {
                if (filter.value) {
                    params = params.append(filter.field, filter.value);
                }
            });
        }
        return params;
    }

    static appendPlantsQuery(tableConfig: TableConfig) {
        tableConfig.removeMultipleFilters('PlantIds');
        if (localStorage.getItem('selectedPlants')) {
            this.selectedPlantsIdsFromStorage = localStorage.getItem('selectedPlants').split(',');
            tableConfig.setMultipleFilters('PlantIds', this.selectedPlantsIdsFromStorage);
        }
        return tableConfig;
    }

    static corePlantsLoaded(currentUser: JWTUser) {
        if (!this.shouldLoadPlants(currentUser)) {
            return true;
        }
        const corePlants = localStorage.getItem('selectedPlants');
        return corePlants !== null && corePlants !== undefined;
    }

    static shouldLoadPlants(currentUser: JWTUser) {
        if (this.plantDropdownRoles.find(role => role === currentUser.role)) {
            return true;
        }
        localStorage.setItem('selectedPlants', '');
        return false;
    }

    static buildBlobFromDataString(data: string) {
        const binaryString = window.atob(data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return new Blob([bytes], {type: 'image/jpeg'});
    }

    static dataURItoBlob(dataURI) {
        let byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0) {
            byteString = atob(dataURI.split(',')[1]);
        } else {
            byteString = unescape(dataURI.split(',')[1]);
        }
        // separate out the mime component
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to a typed array
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ia], {type: mimeString});
    }

    static computeOperationStatus(groupings: any[]) {
        const statuses = {
            [TicketOperationStatus.NEW]: 0,
            [TicketOperationStatus.IN_PROGRESS]: 0,
            [TicketOperationStatus.RESOLVED]: 0,
            [TicketOperationStatus.UNRESOLVED]: 0,
            [TicketOperationStatus.ON_HOLD]: 0
        };

        groupings.forEach(g => {
            statuses[g.status]++;
        });

        if (statuses[TicketOperationStatus.ON_HOLD] >= 1) {
            return TicketOperationStatus.ON_HOLD;
        }


        if (statuses[TicketOperationStatus.NEW] >= 1) {
            return TicketOperationStatus.NEW;
        }

        if (statuses[TicketOperationStatus.RESOLVED] === groupings.length) {
            return TicketOperationStatus.RESOLVED;
        }

        if (statuses[TicketOperationStatus.UNRESOLVED] === groupings.length) {
            return TicketOperationStatus.UNRESOLVED;
        }

        if (statuses[TicketOperationStatus.RESOLVED] + statuses[TicketOperationStatus.UNRESOLVED] === groupings.length) {
            return TicketOperationStatus.RESOLVED;
        }

        return TicketOperationStatus.IN_PROGRESS;
    }

    static arrayLengthValidator(min: number, max: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value || !Array.isArray(control.value)) {
                return {notArray: true}; // The control value is not an array
            }

            const length = control.value.length;
            if (length < min || length > max) {
                return {arrayLength: {requiredMin: min, requiredMax: max, actual: length}};
            }

            return null; // Validation passed
        };
    }

    public static generateUUID() {
        return uuidv4.v7();
    }
}
